import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaClock,
  FaMapMarkerAlt,
  FaPen,
  FaPlusCircle,
  FaProjectDiagram,
  FaSchool,
  FaSearch,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import AdminModalShell from "./AdminModalShell";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const periodOptions = ["Period 1", "Period 2", "Period 3", "Period 4", "Period 5", "Period 6", "Period 7"];


const emptyForm = {
  id: "",
  day: "Monday",
  period: "Period 1",
  time: "09:00 - 09:45",
  className: "",
  section: "",
  subject: "",
  teacher: "",
  room: "",
};

const normalizeSlot = (slot = {}, index = 0) => ({
  id: slot.id || slot.slotId || slot._id || `slot-${index + 1}`,
  day: slot.day || "Monday",
  period: slot.period || slot.periodName || `Period ${index + 1}`,
  time: slot.time || slot.timeRange || "09:00 - 09:45",
  className: slot.className || slot.standard || slot.classLabel || "Class",
  section: slot.section || "A",
  subject: slot.subject || "Subject",
  teacher: slot.teacher || slot.teacherName || "Teacher",
  room: slot.room || slot.roomNumber || "Room",
});

const extractTimetable = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.slots)) return payload.slots;
  if (Array.isArray(payload?.timetable)) return payload.timetable;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const AdminTimetableManagement = ({ createMode = false }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [activeView, setActiveView] = useState("schedule");
  const [apiClasses, setApiClasses] = useState([]);
  const [apiTeachers, setApiTeachers] = useState([]);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchTimetable = async () => {
      setLoading(true);
      try {
        const [timetableResp, classResp, teacherResp] = await Promise.allSettled([
          getCachedOrFetch(getTenantCacheKey("admin:timetable"), () => api.get(API_PATHS.admin.timetable()), { forceRefresh: refreshVersion > 0 }),
          getCachedOrFetch(getTenantCacheKey("admin:classes"), () => api.get(API_PATHS.admin.classes()), { forceRefresh: refreshVersion > 0 }),
          getCachedOrFetch(getTenantCacheKey("admin:teachers"), () => api.get(API_PATHS.admin.teachers()), { forceRefresh: refreshVersion > 0 }),
        ]);

        if (timetableResp.status === "fulfilled") {
          const nextSlots = extractTimetable(timetableResp.value.data).map(normalizeSlot);
          setSlots(nextSlots);
          if (!createMode) {
            setSelectedSlotId(nextSlots[0]?.id || "");
            if (nextSlots[0]) setSelectedDay(nextSlots[0].day);
          } else {
            setSelectedSlotId("");
          }
        } else {
          setSlots([]);
          setSelectedSlotId("");
        }

        if (classResp.status === "fulfilled") {
          const raw = classResp.value?.data?.data ?? classResp.value?.data ?? [];
          const list = Array.isArray(raw) ? raw : (raw?.classes ?? raw?.content ?? []);
          setApiClasses(
            list
              .map((c) => ({
                id: c.id || c._id || c.classId || "",
                name: c.name || c.className || "",
                sections: Array.isArray(c.sections)
                  ? c.sections
                  : String(c.sections || "").split(",").map((s) => s.trim()).filter(Boolean),
                subjects: (Array.isArray(c.subjects)
                  ? c.subjects
                  : String(c.subjects || "").split(",").map((s) => s.trim()).filter(Boolean)).slice(0, 1),
              }))
              .filter((c) => c.name)
          );
        }

        if (teacherResp.status === "fulfilled") {
          const raw = teacherResp.value?.data?.data ?? teacherResp.value?.data ?? [];
          const list = Array.isArray(raw) ? raw : (raw?.teachers ?? raw?.content ?? []);
          setApiTeachers(
            list
              .map((t) => ({
                id: t.id || t._id || t.teacherId || "",
                name: t.name || t.teacherName || t.fullName || "",
              }))
              .filter((t) => t.name)
          );
        }
      } catch {
        setSlots([]);
        setSelectedSlotId("");
        toast.error("Unable to load timetable.");
      } finally {
        setLoading(false);
      }
    };

    fetchTimetable();
  }, [refreshVersion, createMode]);

  useEffect(() => {
    if (createMode) {
      setSelectedSlotId("");
      setForm({ ...emptyForm, day: selectedDay });
      setIsFormOpen(true);
    }
  }, [createMode, selectedDay]);

  const filteredSlots = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return slots.filter((slot) => {
      const matchesDay = slot.day === selectedDay;
      const matchesQuery =
        !query
        || slot.className.toLowerCase().includes(query)
        || slot.section.toLowerCase().includes(query)
        || slot.subject.toLowerCase().includes(query)
        || slot.teacher.toLowerCase().includes(query)
        || slot.room.toLowerCase().includes(query);

      return matchesDay && matchesQuery;
    });
  }, [searchTerm, selectedDay, slots]);

  const selectedSlot = useMemo(
    () => slots.find((slot) => slot.id === selectedSlotId) || null,
    [selectedSlotId, slots]
  );

  useEffect(() => {
    if (createMode || !selectedSlotId || !selectedSlot) {
      setForm({ ...emptyForm, day: selectedDay });
      return;
    }

    setForm({
      id: selectedSlot.id,
      day: selectedSlot.day,
      period: selectedSlot.period,
      time: selectedSlot.time,
      className: selectedSlot.className,
      section: selectedSlot.section,
      subject: selectedSlot.subject,
      teacher: selectedSlot.teacher,
      room: selectedSlot.room,
    });
  }, [selectedDay, selectedSlot, selectedSlotId, createMode]);

  const conflicts = useMemo(() => {
    const teacherMap = new Map();
    const classMap = new Map();
    const foundConflicts = [];

    slots.forEach((slot) => {
      const teacherKey = `${slot.day}|${slot.period}|teacher|${slot.teacher}`;
      const classKey = `${slot.day}|${slot.period}|class|${slot.className}|${slot.section}`;

      if (teacherMap.has(teacherKey)) {
        foundConflicts.push({
          type: "Teacher conflict",
          message: `${slot.teacher} is assigned twice on ${slot.day}, ${slot.period}.`,
          slotId: slot.id,
        });
      } else {
        teacherMap.set(teacherKey, slot.id);
      }

      if (classMap.has(classKey)) {
        foundConflicts.push({
          type: "Class overlap",
          message: `${slot.className} ${slot.section} is assigned twice on ${slot.day}, ${slot.period}.`,
          slotId: slot.id,
        });
      } else {
        classMap.set(classKey, slot.id);
      }
    });

    return foundConflicts;
  }, [slots]);

  const summary = useMemo(() => ({
    totalSlots: slots.length,
    activeDays: new Set(slots.map((slot) => slot.day)).size,
    teachersUsed: new Set(slots.map((slot) => slot.teacher)).size,
    conflicts: conflicts.length,
  }), [conflicts.length, slots]);

  const timetableGrid = useMemo(() => {
    const grid = {};
    days.forEach((day) => {
      grid[day] = {};
      periodOptions.forEach((period) => {
        grid[day][period] = slots.find((slot) => slot.day === day && slot.period === period) || null;
      });
    });
    return grid;
  }, [slots]);

  const panelClass = isDarkMode
    ? "border-slate-800 bg-slate-900 shadow-black/20"
    : "border-slate-200 bg-white shadow-slate-200/60";

  const selectedFormClass = useMemo(
    () => apiClasses.find((c) => c.name === form.className) || null,
    [apiClasses, form.className]
  );

  const formSectionOptions = useMemo(
    () => selectedFormClass?.sections || [],
    [selectedFormClass]
  );

  const formSubjectOptions = useMemo(
    () => selectedFormClass?.subjects || [],
    [selectedFormClass]
  );

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const resetForm = () => {
    setSelectedSlotId("");
    setForm({ ...emptyForm, day: selectedDay });
  };

  const openCreateForm = () => {
    setSelectedSlotId("");
    setForm({ ...emptyForm, day: selectedDay });
    setIsFormOpen(true);
    if (!createMode) {
      navigate("/admin/timetable/create");
    }
  };

  const openEditForm = (slotId) => {
    setSelectedSlotId(slotId);
    const target = slots.find((slot) => slot.id === slotId);
    if (target) {
      setForm({
        id: target.id,
        day: target.day,
        period: target.period,
        time: target.time,
        className: target.className,
        section: target.section,
        subject: target.subject,
        teacher: target.teacher,
        room: target.room,
      });
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setSelectedSlotId("");
    setForm({ ...emptyForm, day: selectedDay });
    setIsFormOpen(false);
    if (createMode) {
      navigate("/admin/timetable");
    }
  };

  const upsertSlot = (nextSlot) => {
    setSlots((current) => {
      const exists = current.some((slot) => slot.id === nextSlot.id);
      if (!exists) return [nextSlot, ...current];
      return current.map((slot) => (slot.id === nextSlot.id ? nextSlot : slot));
    });
    setSelectedSlotId(nextSlot.id);
    setSelectedDay(nextSlot.day);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      day: form.day,
      period: form.period,
      time: form.time.trim(),
      className: form.className.trim(),
      section: form.section.trim(),
      subject: form.subject.trim(),
      teacher: form.teacher.trim(),
      room: form.room.trim(),
    };

    const isEditing = Boolean(form.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.timetableById(form.id), payload)
        : await api.post(API_PATHS.admin.timetable(), payload);

      const savedSlot = normalizeSlot(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? form.id : undefined },
        slots.length
      );
      upsertSlot(savedSlot);
      invalidateCache(getTenantCacheKey("admin:timetable"));
      setSelectedSlotId("");
      setForm({ ...emptyForm, day: selectedDay });
      setIsFormOpen(false);
      if (createMode) navigate("/admin/timetable");
      toast.success(isEditing ? "Timetable slot updated." : "Timetable slot created.");
    } catch {
      toast.error("Failed to save timetable slot. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AdminResponsiveTabs tabs={[{ key: "metrics", label: "Metrics" }, { key: "schedule", label: "Schedule" }, { key: "conflicts", label: `Conflicts${conflicts.length > 0 ? ` (${conflicts.length})` : ""}` }]} activeKey={activeView} onChange={setActiveView} tone="sky" isDarkMode={isDarkMode} />

      <div className={`border-x border-b p-6 shadow-lg ${panelClass}`}>
        {activeView === "metrics" && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Total slots", value: summary.totalSlots },
                { label: "Days used", value: summary.activeDays },
                { label: "Teachers mapped", value: summary.teachersUsed },
                { label: "Conflicts", value: summary.conflicts },
              ].map((item) => (
                <div key={item.label} className={`border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                  <p className="text-xs text-slate-400">{item.label}</p>
                  <p className="mt-2 text-2xl font-bold">{item.value}</p>
                </div>
              ))}
            </div>
            <div className={`overflow-hidden border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left">Metric</th>
                      <th className="px-4 py-3 text-left">Value</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Total timetable slots</td><td className="px-4 py-3">{summary.totalSlots}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Active teaching days</td><td className="px-4 py-3">{summary.activeDays}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Teachers mapped</td><td className="px-4 py-3">{summary.teachersUsed}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Schedule conflicts</td><td className="px-4 py-3">{summary.conflicts}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeView === "conflicts" && (
          <div className="space-y-3">
            {conflicts.length === 0 ? (
              <div className={`rounded-xl border px-4 py-10 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                No conflicts detected in the current timetable.
              </div>
            ) : conflicts.map((conflict, index) => (
              <div key={`${conflict.slotId}-${index}`} className={`rounded-xl border px-4 py-3 text-sm ${isDarkMode ? "border-rose-900/70 bg-rose-950/30 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
                <p className="font-semibold">{conflict.type}</p>
                <p className="mt-1 opacity-90">{conflict.message}</p>
              </div>
            ))}
          </div>
        )}

        {activeView === "schedule" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-2">
                {days.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setSelectedDay(day);
                      if (!selectedSlotId) setForm((current) => ({ ...current, day }));
                    }}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${selectedDay === day ? "bg-sky-500 text-white" : isDarkMode ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                <ModuleImportButton moduleKey="timetable" label="Import timetable" />
                <div className="relative">
                  <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full min-w-[220px] rounded-2xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    placeholder="Search class, teacher, room"
                  />
                </div>
                <button
                  type="button"
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 whitespace-nowrap"
                >
                  <FaPlusCircle /> Add slot
                </button>
              </div>
            </div>

            <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left">Period</th>
                      <th className="px-4 py-3 text-left">Time</th>
                      <th className="px-4 py-3 text-left">Class</th>
                      <th className="px-4 py-3 text-left">Subject</th>
                      <th className="px-4 py-3 text-left">Teacher</th>
                      <th className="px-4 py-3 text-left">Room</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    {loading ? (
                      <tr><td colSpan="6" className={`px-4 py-10 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Loading timetable...</td></tr>
                    ) : filteredSlots.length === 0 ? (
                      <tr><td colSpan="6" className={`px-4 py-10 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No slots for {selectedDay}.</td></tr>
                    ) : filteredSlots.map((slot) => {
                      const hasConflict = conflicts.some((c) => c.slotId === slot.id);
                      return (
                        <tr
                          key={slot.id}
                          onClick={() => openEditForm(slot.id)}
                          className={`cursor-pointer border-t transition ${isDarkMode ? "border-slate-800 hover:bg-slate-800/60" : "border-slate-200 hover:bg-slate-50"} ${selectedSlotId === slot.id ? isDarkMode ? "bg-sky-950/20" : "bg-sky-50" : ""}`}
                        >
                          <td className="px-4 py-3 font-semibold">{slot.period}</td>
                          <td className="px-4 py-3">{slot.time}</td>
                          <td className="px-4 py-3">{slot.className} {slot.section}</td>
                          <td className="px-4 py-3">{slot.subject}</td>
                          <td className="px-4 py-3">{slot.teacher}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>{slot.room}</span>
                              {hasConflict && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDarkMode ? "bg-rose-950/60 text-rose-200" : "bg-rose-100 text-rose-700"}`}>Conflict</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <AdminModalShell isOpen={isFormOpen || createMode} onClose={closeForm} isDarkMode={isDarkMode} pageMode={createMode}>
      <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Timetable form</p>
            <h2 className="mt-2 text-2xl font-bold">{form.id ? "Update timetable slot" : "Create timetable slot"}</h2>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Day</label>
                  <select value={form.day} onChange={(event) => updateForm("day", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100">
                    {days.map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Period</label>
                  <select value={form.period} onChange={(event) => updateForm("period", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100">
                    {periodOptions.map((period) => <option key={period} value={period}>{period}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Time range</label>
                  <input type="text" value={form.time} onChange={(event) => updateForm("time", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="09:00 - 09:45" required />
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Room</label>
                  <input type="text" value={form.room} onChange={(event) => updateForm("room", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="Room 21" required />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Class</label>
                  {apiClasses.length > 0 ? (
                    <select
                      value={form.className}
                      onChange={(event) => {
                        updateForm("className", event.target.value);
                        updateForm("section", "");
                        updateForm("subject", "");
                      }}
                      className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                      required
                    >
                      <option value="">— Select class —</option>
                      {apiClasses.map((c) => (
                        <option key={c.id || c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={form.className} onChange={(event) => updateForm("className", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="Class 10" required />
                  )}
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Section</label>
                  {formSectionOptions.length > 0 ? (
                    <select
                      value={form.section}
                      onChange={(event) => updateForm("section", event.target.value)}
                      className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                      required
                    >
                      <option value="">— Select section —</option>
                      {formSectionOptions.map((sec) => (
                        <option key={sec} value={sec}>Section {sec}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={form.section} onChange={(event) => updateForm("section", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="A" required />
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Subject</label>
                  {formSubjectOptions.length > 0 ? (
                    <select
                      value={form.subject}
                      onChange={(event) => updateForm("subject", event.target.value)}
                      className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                      required
                    >
                      <option value="">— Select subject —</option>
                      {formSubjectOptions.map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={form.subject} onChange={(event) => updateForm("subject", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="Mathematics" required />
                  )}
                </div>
                <div>
                  <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Teacher</label>
                  {apiTeachers.length > 0 ? (
                    <select
                      value={form.teacher}
                      onChange={(event) => updateForm("teacher", event.target.value)}
                      className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                      required
                    >
                      <option value="">— Select teacher —</option>
                      {apiTeachers.map((t) => (
                        <option key={t.id || t.name} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={form.teacher} onChange={(event) => updateForm("teacher", event.target.value)} className="w-full rounded-2xl border border-slate-300 px-3 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100" placeholder="Rahul Patil" required />
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={saving} className="inline-flex items-center rounded-full bg-sky-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:opacity-70">{saving ? "Saving..." : form.id ? "Update timetable slot" : "Create timetable slot"}</button>
                <button type="button" onClick={() => setIsFormOpen(false)} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Cancel</button>
              </div>
            </form>
          </div>
      </AdminModalShell>
    </div>
  );
};

export default AdminTimetableManagement;
